/**
 * Decorative-only autoplay video for landing pages.
 * Encapsulates fallback rules so callers don't have to think:
 *   - Save-Data / 2g connections → poster only
 *   - prefers-reduced-motion → poster only
 *   - <video> error → poster only
 *   - off-screen → pause to save CPU/battery
 *
 * Reserves the box via `aspect-ratio` so layout is stable before media loads.
 */

import React from 'react';
import type { VideoEntry } from '@airhead-games/shared/configs';

const joinClassNames = (...classNames: Array<string | undefined>): string =>
  classNames.filter(Boolean).join(' ');

const useReducedMotion = (): boolean => {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQueryList = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );
    const update = (): void => setReduced(mediaQueryList.matches);
    update();
    mediaQueryList.addEventListener('change', update);
    return () => mediaQueryList.removeEventListener('change', update);
  }, []);
  return reduced;
};

interface NetworkInformationLike {
  saveData?: boolean;
  effectiveType?: string;
}

const useSaveData = (): boolean => {
  const [saveData, setSaveData] = React.useState(false);
  React.useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const connection = (
      navigator as Navigator & { connection?: NetworkInformationLike }
    ).connection;
    if (!connection) return;
    const shouldSave = Boolean(
      connection.saveData ||
      (connection.effectiveType &&
        ['2g', 'slow-2g'].includes(connection.effectiveType))
    );
    setSaveData(shouldSave);
  }, []);
  return saveData;
};

const usePauseOffscreen = (
  ref: React.RefObject<HTMLVideoElement | null>
): void => {
  React.useEffect(() => {
    const video = ref.current;
    if (!video || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting) {
          void video.play().catch(() => {
            /* autoplay can be denied; poster remains visible */
          });
        } else {
          video.pause();
        }
      },
      { threshold: 0 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [ref]);
};

interface SplashVideoProps {
  entry: VideoEntry;
  className?: string;
  posterAlt?: string;
  rounded?: boolean;
}

const SplashVideo: React.FC<SplashVideoProps> = ({
  entry,
  className,
  posterAlt = '',
  rounded = true,
}) => {
  const reducedMotion = useReducedMotion();
  const saveData = useSaveData();
  const [errored, setErrored] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  usePauseOffscreen(videoRef);

  const usePosterOnly = reducedMotion || saveData || errored;

  const wrapperClassName = joinClassNames(
    'relative overflow-hidden bg-black/40',
    rounded ? 'rounded-2xl' : undefined,
    className
  );

  return (
    <div
      className={wrapperClassName}
      style={{ aspectRatio: entry.aspectRatio }}
    >
      {usePosterOnly ? (
        <img
          src={entry.poster}
          alt={posterAlt}
          className="block h-full w-full object-cover"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <video
          ref={videoRef}
          poster={entry.poster}
          className="block h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          disablePictureInPicture
          disableRemotePlayback
          onError={() => setErrored(true)}
        >
          {entry.sources.map((source) => (
            <source
              key={source.src}
              src={source.src}
              type={source.type}
              media={source.media}
            />
          ))}
        </video>
      )}
    </div>
  );
};

export default SplashVideo;
